import { useCallback, useEffect, useState } from "react";
import { Plus, ArrowRight, Camera, Ruler, HardHat, FileText, Receipt, Loader2, Download, Upload, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, formatApiErrorDetail } from "@/lib/api";
import { usePartner } from "../PartnerContext";
import { SMALL_STEPS, euro } from "@/data/partnerConfig";
import { Card, PageHeader, Btn, StatusBadge, SlideOver, InfoRow, Placeholder, Field, inputCls } from "../ui";

const QUOTE_LABELS = { recu: "Devis reçu — en attente d'accord client", accepte: "Devis accepté par le client" };
const INVOICE_LABELS = { recue: "Facture reçue — à régler", payee: "Facture payée" };

const docUrl = (id) => `${BACKEND}/api/phase3/small-works/documents/${id}/download`;
const uploadSmallWorkDoc = (id, file, kind) => {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  return api.post(`/phase3/small-works/${id}/documents`, form).then((r) => r.data);
};

const StepFlow = ({ current, testid }) => {
  const idx = SMALL_STEPS.indexOf(current);
  return (
    <ol data-testid={testid} className="flex flex-wrap items-center gap-y-2">
      {SMALL_STEPS.map((s, i) => (
        <li key={s} className="flex items-center">
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
              i === idx ? "bg-cognac text-white" : i < idx ? "bg-cognac/15 text-cognac-dark" : "bg-[#F1F3F4] text-stone-400"
            }`}
          >
            {s}
          </span>
          {i < SMALL_STEPS.length - 1 && <ArrowRight className="mx-1 h-3 w-3 text-stone-300" />}
        </li>
      ))}
    </ol>
  );
};

const SmallWorkDetail = ({ item, artisan, backend, companies, onUpdated }) => {
  const [measures, setMeasures] = useState(item.measures || "");
  const [artisanId, setArtisanId] = useState(item.artisan_company_id || "");
  const [quoteFile, setQuoteFile] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState(item.quote_amount_ht || "");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState(item.invoice_amount_ht || "");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setMeasures(item.measures || ""); setArtisanId(item.artisan_company_id || "");
    setQuoteFile(null); setQuoteAmount(item.quote_amount_ht || "");
    setInvoiceFile(null); setInvoiceAmount(item.invoice_amount_ht || "");
  }, [item.id]);

  const guard = () => {
    if (!backend) {
      toast.error("Fonctionnalité disponible avec un compte partenaire réel connecté.");
      return false;
    }
    return true;
  };

  const patch = async (body) => {
    const { data } = await api.patch(`/phase3/small-works/${item.id}`, { ...body, version: item.version });
    onUpdated(data);
    return data;
  };

  const run = (key, action, successMsg) => async () => {
    if (!guard()) return;
    setBusy(key);
    try { await action(); if (successMsg) toast.success(successMsg); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveNeed = run("need", () => patch({ measures }), "Métrés enregistrés.");
  const assignArtisan = run("artisan", async () => {
    if (!artisanId) throw { response: { data: { detail: "Sélectionnez un artisan." } } };
    await patch({ artisan_company_id: artisanId });
  }, "Artisan affecté.");
  const saveQuote = run("quote", async () => {
    if (!quoteFile || !quoteAmount) throw { response: { data: { detail: "PDF du devis et montant HT requis." } } };
    const doc = await uploadSmallWorkDoc(item.id, quoteFile, "quote");
    await patch({ quote_amount_ht: Number(quoteAmount), quote_file_id: doc.id });
    setQuoteFile(null);
  }, "Devis enregistré.");
  const acceptQuote = run("accept-quote", () => patch({ quote_status: "accepte" }), "Devis marqué comme accepté.");
  const saveInvoice = run("invoice", async () => {
    if (!invoiceFile || !invoiceAmount) throw { response: { data: { detail: "PDF de la facture et montant HT requis." } } };
    const doc = await uploadSmallWorkDoc(item.id, invoiceFile, "invoice");
    await patch({ invoice_amount_ht: Number(invoiceAmount), invoice_file_id: doc.id });
    setInvoiceFile(null);
  }, "Facture enregistrée.");
  const markPaid = run("paid", () => patch({ invoice_status: "payee" }), "Facture marquée payée — dossier terminé.");

  const idx = SMALL_STEPS.indexOf(item.step);
  const isLast = idx < 0 || idx >= SMALL_STEPS.length - 1;
  const nextStep = run("step", () => patch({ step: SMALL_STEPS[idx + 1] }), "Étape mise à jour.");
  const addPhoto = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !guard()) return;
    setBusy(`photo-${kind}`);
    try {
      const doc = await uploadSmallWorkDoc(item.id, file, kind === "before" ? "photo_before" : "photo_after");
      const { data } = await api.post(`/phase3/small-works/${item.id}/photos`, { kind, file_id: doc.id });
      onUpdated(data);
      toast.success("Photo ajoutée.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <StepFlow current={item.step} testid="small-detail-flow" />
        {!isLast && (
          <Btn variant="ghost" testid="small-next-step" disabled={busy === "step"} onClick={nextStep} className="shrink-0">
            Étape suivante <ArrowRight className="h-3.5 w-3.5" />
          </Btn>
        )}
      </div>

      <div className="mt-5">
        <InfoRow label="Client" value={item.client_name} />
        <InfoRow label="Référence" value={item.reference} testid="small-detail-reference" />
        <InfoRow label="Adresse" value={item.address || "—"} />
        <InfoRow label="Statut" value={<StatusBadge status={item.step} testid="small-detail-status" />} />
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><Ruler className="h-3.5 w-3.5" /> Besoin & métrés</p>
      <div className="mt-2 space-y-2">
        <textarea data-testid="small-measures-input" rows={2} className={inputCls} placeholder="Besoin, métrés, contraintes..." value={measures} onChange={(e) => setMeasures(e.target.value)} />
        <Btn variant="ghost" testid="small-save-measures" disabled={busy === "need"} onClick={saveNeed}>Enregistrer</Btn>
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><HardHat className="h-3.5 w-3.5" /> Artisan</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <select data-testid="small-artisan-select" className={`${inputCls} max-w-xs`} value={artisanId} onChange={(e) => setArtisanId(e.target.value)}>
          <option value="">Sélectionner un artisan</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <Btn variant="ghost" testid="small-artisan-assign" disabled={busy === "artisan"} onClick={assignArtisan}>Affecter</Btn>
      </div>
      {artisan && <p className="mt-2 text-xs text-stone-500">Actuellement affecté : <span className="font-semibold text-navy">{artisan.name}</span></p>}

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><FileText className="h-3.5 w-3.5" /> Devis</p>
          {item.quote_file_id ? (
            <div className="mt-2 space-y-1.5 text-sm">
              <a href={docUrl(item.quote_file_id)} target="_blank" rel="noreferrer" data-testid="small-quote-download" className="inline-flex items-center gap-1 font-semibold text-petrol hover:underline"><Download className="h-3.5 w-3.5" /> Voir le devis</a>
              <p className="font-semibold text-navy">{euro(item.quote_amount_ht)}</p>
              <p className="text-xs text-stone-500">{QUOTE_LABELS[item.quote_status]}</p>
              {item.quote_status === "recu" && <Btn variant="copper" testid="small-quote-accept" disabled={busy === "accept-quote"} onClick={acceptQuote} className="mt-1">Marquer accepté</Btn>}
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <input data-testid="small-quote-file" type="file" accept="application/pdf" className={inputCls} onChange={(e) => setQuoteFile(e.target.files?.[0] || null)} />
              <input data-testid="small-quote-amount" type="number" placeholder="Montant HT" className={inputCls} value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} />
              <Btn variant="ghost" testid="small-quote-save" disabled={busy === "quote"} onClick={saveQuote}>Enregistrer le devis</Btn>
            </div>
          )}
        </div>
        <div>
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><Receipt className="h-3.5 w-3.5" /> Facture</p>
          {item.invoice_file_id ? (
            <div className="mt-2 space-y-1.5 text-sm">
              <a href={docUrl(item.invoice_file_id)} target="_blank" rel="noreferrer" data-testid="small-invoice-download" className="inline-flex items-center gap-1 font-semibold text-petrol hover:underline"><Download className="h-3.5 w-3.5" /> Voir la facture</a>
              <p className="font-semibold text-navy">{euro(item.invoice_amount_ht)}</p>
              <p className="text-xs text-stone-500">{INVOICE_LABELS[item.invoice_status]}</p>
              {item.invoice_status === "recue" && <Btn variant="copper" testid="small-invoice-paid" disabled={busy === "paid"} onClick={markPaid} className="mt-1">Marquer payée</Btn>}
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <input data-testid="small-invoice-file" type="file" accept="application/pdf" className={inputCls} onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} />
              <input data-testid="small-invoice-amount" type="number" placeholder="Montant HT" className={inputCls} value={invoiceAmount} onChange={(e) => setInvoiceAmount(e.target.value)} />
              <Btn variant="ghost" testid="small-invoice-save" disabled={busy === "invoice"} onClick={saveInvoice}>Enregistrer la facture</Btn>
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><Camera className="h-3.5 w-3.5" /> Photos avant / après</p>
      <div className="mt-2 grid grid-cols-2 gap-3">
        {["before", "after"].map((kind) => {
          const photos = kind === "before" ? item.photos_before : item.photos_after;
          return (
            <div key={kind}>
              <p className="mb-1.5 text-xs font-semibold text-stone-500">{kind === "before" ? "Avant" : "Après"} ({photos.length})</p>
              <div className="grid grid-cols-3 gap-1.5">
                {photos.length === 0 && <Placeholder testid={`small-photos-${kind}-empty`} className="col-span-3 py-3">—</Placeholder>}
                {photos.map((id) => (
                  <a key={id} href={docUrl(id)} target="_blank" rel="noreferrer" data-testid={`small-photo-${id}`}>
                    <img src={docUrl(id)} alt="" className="aspect-square rounded-lg border border-[#E3E8E9] object-cover" />
                  </a>
                ))}
              </div>
              <label className="mt-1.5 inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-petrol">
                <Upload className="h-3.5 w-3.5" /> Ajouter
                <input data-testid={`small-photo-${kind}-input`} type="file" accept="image/*" className="hidden" disabled={busy === `photo-${kind}`} onChange={addPhoto(kind)} />
              </label>
            </div>
          );
        })}
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><Clock className="h-3.5 w-3.5" /> Historique</p>
      <ul className="mt-2 space-y-1.5 border-l-2 border-cognac/30 pl-4 text-xs text-stone-600" data-testid="small-history">
        {item.history.map((h, i) => <li key={i}>{h.date ? `${h.date} — ` : ""}{h.text}</li>)}
      </ul>
    </>
  );
};

export default function PartnerSmallWorks() {
  const { backend } = usePartner();
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ client_name: "", address: "", need: "" });
  const [savingCreate, setSavingCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/phase3/small-works"), api.get("/phase3/companies")])
      .then(([sw, co]) => { setList(sw.data); setCompanies(co.data); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [backend]);

  useEffect(() => { load(); }, [load]);

  const selected = list.find((i) => i.id === selectedId) || null;
  const artisan = selected ? companies.find((c) => c.id === selected.artisan_company_id) : null;
  const applyUpdate = (updated) => setList((cur) => cur.map((i) => (i.id === updated.id ? updated : i)));

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!backend) return toast.error("Création disponible uniquement avec un compte partenaire réel connecté.");
    if (createForm.client_name.trim().length < 2) return toast.error("Le nom du client est requis.");
    setSavingCreate(true);
    try {
      const { data } = await api.post("/phase3/small-works", createForm);
      setList((cur) => [data, ...cur]);
      setCreating(false);
      setCreateForm({ client_name: "", address: "", need: "" });
      setSelectedId(data.id);
      toast.success("Dossier créé.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSavingCreate(false); }
  };

  return (
    <div data-testid="partner-small-works">
      <PageHeader
        eyebrow="Interventions ciblées"
        title="Petits travaux"
        sub="Des dossiers légers, sans mission complète de maîtrise d'œuvre : un besoin, un artisan, un devis, des travaux, une facture."
        actions={
          <Btn variant="copper" testid="small-create" onClick={() => (backend ? setCreating(true) : toast.error("Création disponible uniquement avec un compte partenaire réel connecté."))}>
            <Plus className="h-4 w-4" /> Nouveau dossier
          </Btn>
        }
      />

      <Card className="overflow-hidden">
        <div className="h-1 bg-cognac" />
        <div className="p-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Le parcours d'un dossier</p>
          <div className="mt-3"><StepFlow current="" testid="small-workflow" /></div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-petrol" /></div>
      ) : (
        <div data-testid="small-list" className="mt-4 grid gap-3 lg:grid-cols-3">
          {list.length === 0 && <Placeholder testid="small-list-empty" className="lg:col-span-3">Aucun dossier « petits travaux » pour l'instant.</Placeholder>}
          {list.map((s) => {
            const pro = companies.find((c) => c.id === s.artisan_company_id);
            return (
              <button
                key={s.id}
                data-testid={`small-card-${s.reference}`}
                onClick={() => setSelectedId(s.id)}
                className="rounded-2xl border border-[#E3E8E9] bg-white p-5 text-left transition-colors hover:border-cognac/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] font-bold text-cognac-dark">{s.reference}</p>
                    <p className="mt-0.5 font-semibold text-navy">{s.need || "Besoin à préciser"}</p>
                    <p className="text-xs text-stone-500">{s.client_name}</p>
                  </div>
                  <StatusBadge status={s.step} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-stone-400">Artisan</p>
                    <p className="mt-0.5 font-semibold text-navy">{pro?.name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-stone-400">Montant travaux</p>
                    <p className="mt-0.5 font-semibold text-navy">{s.quote_amount_ht ? euro(s.quote_amount_ht) : "—"}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-stone-500">
                  <Camera className="h-3.5 w-3.5" /> {s.photos_before.length} avant · {s.photos_after.length} après
                  <span className="ml-auto inline-flex items-center gap-1"><Receipt className="h-3.5 w-3.5" /> {INVOICE_LABELS[s.invoice_status] || "—"}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <SlideOver open={!!selected} onClose={() => setSelectedId(null)} testid="small-detail" eyebrow={selected?.reference} title={selected?.need || "Dossier"}>
        {selected && <SmallWorkDetail item={selected} artisan={artisan} backend={backend} companies={companies} onUpdated={applyUpdate} />}
      </SlideOver>

      <SlideOver open={creating} onClose={() => setCreating(false)} testid="small-create-form" eyebrow="Nouveau dossier" title="Petits travaux">
        <form onSubmit={submitCreate} className="space-y-3">
          <Field label="Nom du client *"><input data-testid="small-create-client" className={inputCls} value={createForm.client_name} onChange={(e) => setCreateForm({ ...createForm, client_name: e.target.value })} /></Field>
          <Field label="Adresse"><input data-testid="small-create-address" className={inputCls} value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} /></Field>
          <Field label="Besoin"><input data-testid="small-create-need" className={inputCls} value={createForm.need} onChange={(e) => setCreateForm({ ...createForm, need: e.target.value })} /></Field>
          <Btn type="submit" variant="copper" testid="small-create-submit" disabled={savingCreate} className="w-full">{savingCreate ? "Création..." : "Créer le dossier"}</Btn>
        </form>
      </SlideOver>
    </div>
  );
}
